import { nanoid } from '@reduxjs/toolkit';
import { DEFAULT_HEADER_CONFIG, HEADER_PRESETS } from 'Modulor/components/Header/presets';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';

import type { ConfigStackEntry, HeaderConfig, HeaderContextValue, PresetId } from './types';

export type { ConfigStackEntry, HeaderConfig, HeaderContextValue, NavIcon, PresetId } from './types';

export const HeaderContext = createContext<HeaderContextValue | undefined>(undefined);

interface HeaderProviderProps {
  children: ReactNode;
}

export const HeaderProvider: FC<HeaderProviderProps> = ({ children }) => {
  const stackRef = useRef<ConfigStackEntry[]>([]);
  const [currentConfig, setCurrentConfig] = useState<HeaderConfig>(DEFAULT_HEADER_CONFIG);

  const mergeConfigStack = useCallback(() => {
    // Keep last config when stack is empty to avoid flash during route transitions
    if (stackRef.current.length === 0) {
      return;
    }
    const merged = stackRef.current.reduce(
      (acc, entry) => ({
        ...acc,
        ...entry.config,
      }),
      {
        ...DEFAULT_HEADER_CONFIG,
      },
    );
    setCurrentConfig(merged);
  }, []);

  const pushConfig = useCallback(
    (config: Partial<HeaderConfig>) => {
      const id = nanoid();
      stackRef.current = [
        ...stackRef.current,
        {
          id,
          config,
        },
      ];
      mergeConfigStack();
      return id;
    },
    [mergeConfigStack],
  );

  const popConfig = useCallback(
    (id: string) => {
      if (stackRef.current.length === 0) {
        return;
      }
      stackRef.current = stackRef.current.filter((e) => e.id !== id);
      mergeConfigStack();
    },
    [mergeConfigStack],
  );

  const contextValue = useMemo(
    () => ({
      config: currentConfig,
      pushConfig,
      popConfig,
    }),
    [currentConfig, pushConfig, popConfig],
  );

  return <HeaderContext.Provider value={contextValue}>{children}</HeaderContext.Provider>;
};

export const resolvePresetConfig = (presetId: PresetId): Partial<HeaderConfig> => {
  const preset = HEADER_PRESETS[presetId];
  if (!preset) {
    console.warn(`[HeaderProvider] Unknown preset ID: "${presetId}". Using DEFAULT_HEADER_CONFIG.`);
    return {};
  }
  return preset;
};
